import { useCallback, useEffect, useMemo, useState } from 'react';
import { isAfter, isValid } from 'date-fns';

import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { KnmResultDecisionStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionStatusEnum';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IErknmKindResultDecisionSelectOption } from 'CorePackage/CatalogsModule/interfaces/IErknmKindResultDecisionSelectOption';
import { ResultDecisionStatus } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ResultDecisionStatus';
import { getRequirementsWithNonFixedViolations } from 'KnmErknmPackage/ErknmViolationsModule/common/getRequirementsWithNonFixedViolations';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { IKnmErknmOrganizationTemplateSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmOrganizationTemplateSelectOption';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { generateOrganizationTemplateText } from 'KnmErknmPackage/MainModule/common/generateOrganizationTemplateText';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { KnmResultDecisionAgreementStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionAgreementStatusEnum';
import { IKnmErknmFormOrganizationAdd } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormOrganizationAdd';
import { resultDecisionSubjectTypeOptions } from 'KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActSubject/components/ErknmSubjectAddDecision';

import { IKnmErknmFormAct } from '../../MainModule/interfaces/IKnmErknmFormAct';
import { IKnmErknmFormEvent } from '../../MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormSubject } from '../../MainModule/interfaces/IKnmErknmFormSubject';
import { IKnmErknmFormViolatedRequirementsNpa } from '../../MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { ErknmResultDecisionsItemContent } from './ErknmResultDecisionsItemContent/ErknmResultDecisionsItemContent';
import { ErknmResultDecisionsItemPaperlessContent } from './ErknmResultDecisionsItemPaperlesContent/ErknmResultDecisionsItemPaperlessContent';
import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { KnmResultItemSubjectTypesEnum } from './ErknmResultItemSubjectInfo';
import { isProductSalesSuspensionLocked } from '../../MainModule/common/getErknmProductSalesSuspensionValidate';
import { getProductSalesToday, parseProductSalesFormDate } from '../../MainModule/common/productSalesSuspensionDates';

const COLLAPSE_TITLE_BASE = 'Решение';

interface IErknmResultDecisionsItemProps {
  formRef: IKnmFormRef | undefined;
  watchOnly: boolean;
  id: string;
  name: string;
  decisionIndex: number;
  value: IKnmErknmFormResultDecision;
  kindKnmCode: KnmKindCodesEnum | undefined;
  knoOrganizationTitle: IKnoOrganizationDictionary['title'] | undefined;
  knoOrganizationPhone: IKnoOrganizationDictionary['phone'] | undefined;
  knoOrganizationAddress: IKnoOrganizationDictionary['address'] | undefined;
  organizationsTemplatesSelectOptions: IKnmErknmOrganizationTemplateSelectOption[] | undefined;
  resultDecisions: IKnmErknmFormResultDecision[] | undefined;
  onRemove: () => void;
  addRequirementsNpa: (decisionIndex: number, fieldName: string) => void;
  actsRequirementsNpa: IKnmErknmFormViolatedRequirementsNpa[];
  acts: IKnmErknmFormAct[] | undefined;
  eventsErknm?: IKnmErknmFormEvent[] | undefined;
  organizations?: IKnmErknmFormSubject[] | undefined;
  isLaterThanViolationsInProd?: boolean;
  isShow4127: boolean;
  isPaperless: boolean;
  actsValue: IKnmErknmFormAct[] | undefined;
  agreements: IErknmFormAgreement[] | undefined;
  isLater4111ForDecision: boolean;
  isExpandAllInPrescription?: boolean;
}

export const ErknmResultDecisionsItem = ({
  formRef,
  watchOnly,
  id,
  name,
  decisionIndex,
  value,
  kindKnmCode,
  knoOrganizationTitle,
  knoOrganizationPhone,
  knoOrganizationAddress,
  organizationsTemplatesSelectOptions,
  resultDecisions,
  onRemove,
  addRequirementsNpa,
  actsRequirementsNpa,
  acts,
  eventsErknm,
  organizations,
  isLaterThanViolationsInProd,
  isShow4127,
  isPaperless,
  actsValue,
  agreements,
  isLater4111ForDecision,
  isExpandAllInPrescription,
}: IErknmResultDecisionsItemProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const update = formRef?.current.mutators.update;

  const {
    isNew: isNewResultDecision,
    kindDecision,
    organizationTemplate,
    organization,
    violatedMandatoryRequirements,
  } = value;
  const kindDecisionCode = kindDecision?.code;
  const isD10 = kindDecisionCode === KindResultDecisionCodesEnum.D_10;
  const today = getProductSalesToday();
  const resumptionDate = parseProductSalesFormDate(value.productSalesResumption?.resumptionDate);
  const isD10CancellationSigned = isD10 && !!value.resumptionSigned && !!resumptionDate && isValid(resumptionDate);
  const isPendingD10Cancellation = isD10CancellationSigned && !!resumptionDate && isAfter(resumptionDate, today);
  const displayStatus = isD10CancellationSigned ? KnmResultDecisionStatusEnum.CANCELED : value.status;
  const kindDecisionBlockId = `${id}.kindDecision`;
  const [isDecisionCancelled, setIsDecisionCancelled] = useState<boolean>(false);
  const violatedRequirementsNpaFieldName = `${name}.violatedMandatoryRequirements`;
  const [autoFilledOrganizationTemplateValue, setAutoFilledOrganizationTemplateValue] = useState(
    organizationTemplate?.value,
  );

  const lateFieldName = value.organization?.guid
    ? `organization/${value.organization.guid}/resultDecisions/${value.guid}`
    : '';

  useEffect(() => {
    if (value.paperless || organizationTemplate?.value === autoFilledOrganizationTemplateValue) {
      return;
    }
    if (
      (kindDecision?.value === KindResultDecisionCodesEnum.D_7 ||
        kindDecision?.value === KindResultDecisionCodesEnum.D_1) &&
      !!organizationTemplate?.value
    ) {
      if (!remove || !update || !push) {
        return;
      }
      const requirementsWithNonFixedViolations = getRequirementsWithNonFixedViolations(actsRequirementsNpa);
      const violatedMandatoryRequirementsLength = violatedMandatoryRequirements?.length ?? 0;
      const maxLength = Math.max(violatedMandatoryRequirementsLength, requirementsWithNonFixedViolations.length);

      for (let idx = 0; idx < maxLength; idx += 1) {
        if (idx < requirementsWithNonFixedViolations.length) {
          if (idx < violatedMandatoryRequirementsLength) {
            update(violatedRequirementsNpaFieldName, idx, requirementsWithNonFixedViolations[idx]);
          } else {
            push(violatedRequirementsNpaFieldName, requirementsWithNonFixedViolations[idx]);
          }
        } else {
          remove(violatedRequirementsNpaFieldName, idx);
        }
      }
      setAutoFilledOrganizationTemplateValue(organizationTemplate?.value);
    }
  }, [organizationTemplate?.value, kindDecision?.value]);

  const transformKindResultDecisionsSelectOptions = useCallback(
    (arr: IErknmKindResultDecisionSelectOption[] | undefined) => {
      return arr
        ?.filter(kindResultDecisionSelectOption => {
          let organizationsWithCreatedByActGuids: string[] = [];

          if (organizations) {
            organizationsWithCreatedByActGuids = organizations.map(org => org.guid || '');
          }
          if (actsValue) {
            actsValue.forEach(act => {
              if (act.organization && !organizationsWithCreatedByActGuids.find(org => org === act.organization?.guid)) {
                organizationsWithCreatedByActGuids.push(act.organization.guid || '');
              }
            });
          }

          const isNeedShowPrescriptionByOrganization = !!organizationsWithCreatedByActGuids?.filter(
            org =>
              !resultDecisions?.find(
                rd =>
                  rd.organization?.guid === org &&
                  rd?.status !== KnmResultDecisionStatusEnum.CANCELED &&
                  (rd.kindDecision?.code === KindResultDecisionCodesEnum.D_1 ||
                    rd.kindDecision?.code === KindResultDecisionCodesEnum.D_7 ||
                    rd.kindDecision?.code === KindResultDecisionCodesEnum.D_10),
              ),
          ).length;

          if (isPaperless) {
            return isNeedShowPrescriptionByOrganization
              ? true
              : kindResultDecisionSelectOption.code !== KindResultDecisionCodesEnum.D_1 &&
                  kindResultDecisionSelectOption.code !== KindResultDecisionCodesEnum.D_7 &&
                  kindResultDecisionSelectOption.code !== KindResultDecisionCodesEnum.D_10;
          }

          return true;
        })
        .map(kindResultDecisionSelectOption => {
          const isDisablePrescriptionByAct = !actsValue?.find(
            i =>
              i.familiarizationNotifications &&
              i.familiarizationNotifications?.length > 0 &&
              !!i.violatedMandatoryRequirements,
          );

          const isPrescription =
            kindResultDecisionSelectOption.code === KindResultDecisionCodesEnum.D_1 ||
            kindResultDecisionSelectOption.code === KindResultDecisionCodesEnum.D_7;

          const isDisabled = isPaperless ? isPrescription && isDisablePrescriptionByAct : false;

          return {
            ...kindResultDecisionSelectOption,
            isDisabled,
          };
        });
    },
    [kindDecision?.id, organization?.listId, resultDecisions, isShow4127, actsValue, organizations, isPaperless],
  );

  useEffect(() => {
    if (
      kindDecisionCode !== KindResultDecisionCodesEnum.D_7 &&
      kindDecisionCode !== KindResultDecisionCodesEnum.D_1 &&
      kindDecisionCode !== KindResultDecisionCodesEnum.D_10
    ) {
      formRef?.current.change(`${name}.injunction` as keyof IKnmErknmForm);
    }
    if (
      kindDecisionCode === KindResultDecisionCodesEnum.D_1 ||
      kindDecisionCode === KindResultDecisionCodesEnum.D_2 ||
      kindDecisionCode === KindResultDecisionCodesEnum.D_7 ||
      kindDecisionCode === KindResultDecisionCodesEnum.D_9 ||
      kindDecisionCode === KindResultDecisionCodesEnum.D_10
    ) {
      formRef?.current.change(`${name}.noteLawsuits` as keyof IKnmErknmForm);
    }
  }, [formRef, kindDecisionCode, name]);

  const canceledDecisionsInJudgments = formRef?.current
    .getFieldState('judgments')
    ?.value?.filter(x => x.judgmentSubjectData)
    ?.flatMap(x => {
      return x.judgmentSubjectData
        ?.flatMap(a => {
          return a.cancellationActsAndDecisions?.map(b => {
            return b.decisionGuid;
          });
        })
        .filter(a => a?.length);
    })
    .filter(x => x?.length);

  const canceledDecisionsInCancelledJudgments = formRef?.current
    .getFieldState('judgments')
    ?.value?.filter(x => x.canceledJudgments)
    ?.flatMap(x => {
      return x.canceledJudgments
        ?.flatMap(j => {
          return j.judgmentSubjectData
            ?.flatMap(a => {
              return a.cancellationActsAndDecisions
                ?.filter(b => b.decisionGuid && b.newCancellationInformation?.name === 'Отмена решения')
                .map(b => {
                  return b.decisionGuid;
                });
            })
            .filter(a => a?.length);
        })
        .filter(j => j?.length);
    })
    .filter(x => x?.length);

  useEffect(() => {
    canceledDecisionsInJudgments?.forEach(x => {
      if (x === value.guid) {
        setIsDecisionCancelled(true);
      }
    });
    canceledDecisionsInCancelledJudgments?.forEach(x => {
      if (x === value.guid) {
        if (
          canceledDecisionsInJudgments &&
          canceledDecisionsInJudgments.filter(a => a === value.guid).length >
            canceledDecisionsInCancelledJudgments?.filter(a => a === value.guid).length
        ) {
          setIsDecisionCancelled(true);
        } else {
          setIsDecisionCancelled(false);
        }
      }
    });
  }, [
    canceledDecisionsInJudgments,
    canceledDecisionsInCancelledJudgments,
    formRef?.current.getFieldState('judgments'),
  ]);

  useEffect(() => {
    if (actsValue?.length) {
      if (
        (kindDecisionCode === KindResultDecisionCodesEnum.D_7 ||
          kindDecisionCode === KindResultDecisionCodesEnum.D_1 ||
          kindDecisionCode === KindResultDecisionCodesEnum.D_10) &&
        value.act?.id
      ) {
        const selectedAct = actsValue.find(a => a.guid === value.act?.id);

        if (!selectedAct?.familiarizationNotifications || selectedAct?.familiarizationNotifications.length === 0) {
          formRef?.current.change(`${name}.kindDecision` as keyof IKnmErknmForm);
          formRef?.current.change(`${name}.act` as keyof IKnmErknmForm);
          formRef?.current.change(`${name}.numberAct` as keyof IKnmErknmForm);
          formRef?.current.change(`${name}.numberDecision` as keyof IKnmErknmForm);
        }
      }
    }
  }, [JSON.stringify(actsValue)]);

  const handleKindDecisionChange = useCallback(
    (v: IErknmKindResultDecisionSelectOption) => {
      formRef?.current.change(`${name}.links` as keyof IKnmErknmForm);

      if (
        isPaperless &&
        (v?.code === KindResultDecisionCodesEnum.D_7 ||
          v?.code === KindResultDecisionCodesEnum.D_1 ||
          v?.code === KindResultDecisionCodesEnum.D_10)
      ) {
        if (acts?.length === 1) {
          formRef?.current.change(`${name}.act` as keyof IKnmErknmForm, {
            id: acts[0].guid || '',
            value: acts[0].guid,
            label: `${acts[0].numberAct} - ${generateOrganizationTemplateText(
              acts[0].organization?.inn || undefined,
              acts[0].organization?.ogrn || undefined,
              acts[0].organization?.organizationName || undefined,
              acts[0].organization?.rafpNza || undefined,
            )}`,
          });
          const organizationAdd: IKnmErknmFormOrganizationAdd | undefined = acts[0]?.organization;

          formRef?.current.change(`${name}.organization` as keyof IKnmErknmForm, organizationAdd);
          formRef?.current.change(`${name}.organizationTemplate` as keyof IKnmErknmForm, acts[0]?.organizationTemplate);

          const countResultDecisionWithSelectedAct =
            resultDecisions?.filter(rd => rd?.act?.id === acts[0].guid).length || 0;

          formRef?.current.change(
            `${name}.numberDecision` as keyof IKnmErknmForm,
            `${acts[0].numberAct}-${countResultDecisionWithSelectedAct + 1}`,
          );
          if (
            organizationAdd?.subjectType?.code &&
            isLater4111ForDecision &&
            organizationAdd.subjectType.code === KnmResultItemSubjectTypesEnum.UR
          ) {
            formRef?.current.change(
              `${name}.subjectTypeFounder` as keyof IKnmErknmForm,
              resultDecisionSubjectTypeOptions[0],
            );
          }
        }
      } else {
        formRef?.current.change(`${name}.act` as keyof IKnmErknmForm);
        formRef?.current.change(`${name}.numberAct` as keyof IKnmErknmForm);
        formRef?.current.change(`${name}.numberDecision` as keyof IKnmErknmForm);
        formRef?.current.change(`${name}.organization` as keyof IKnmErknmForm);
        formRef?.current.change(`${name}.organizationTemplate` as keyof IKnmErknmForm);
      }
      formRef?.current.change(`${name}.prescriptionForUnidentifiedPersons` as keyof IKnmErknmForm);
      formRef?.current.change(`${name}.productsDescription` as keyof IKnmErknmForm);
      formRef?.current.change(`${name}.validTo` as keyof IKnmErknmForm);
      formRef?.current.change(`${name}.isIndefinite` as keyof IKnmErknmForm);
      formRef?.current.change(`${name}.productSalesResumption` as keyof IKnmErknmForm);
    },
    [formRef, acts, isPaperless],
  );

  const getOnClose = useCallback(
    (val: IKnmErknmFormResultDecision) => {
      if (val.kindDecision?.code === KindResultDecisionCodesEnum.D_10 && isProductSalesSuspensionLocked(val)) {
        return undefined;
      }

      if (isPaperless) {
        return val.signed ? undefined : onRemove;
      }

      return val.status === KnmResultDecisionStatusEnum.COMPLETED ? undefined : onRemove;
    },
    [isPaperless, onRemove],
  );

  /**
   * Функция определения дополнительного статуса по соглашению
   */
  const agreementStatus = useMemo(() => {
    if (agreements) {
      const agreement = agreements.find(agr => agr.prescription?.id === value.guid && agr.isNew);

      if (agreement) return KnmResultDecisionAgreementStatusEnum.AGREEMENT_DRAFT;
    }

    return value.agreementStatus;
  }, [value, agreements]);

  const hasError = checkHasErrorInCollapseBlock(formRef, name);

  const warningText = useMemo(() => {
    if (kindDecisionCode === KindResultDecisionCodesEnum.D_9) {
      return 'Данный вид решения не предназначен для внесения сведений о предписании';
    }
    if (kindDecisionCode === KindResultDecisionCodesEnum.D_10) {
      return 'Этот вид решения предназначен для предписания о приостановке реализации продукции. Для предписания об устранении выявленных нарушений используйте вид решения «Сведения о предписании об устранении выявленных нарушений…»';
    }
    if (isPaperless) {
      return 'Выбор значения "предписание" возможен после добавления уведомления об акте';
    }

    return undefined;
  }, [isPaperless, kindDecisionCode]);

  return (
    <KnmCollapse
      theme="fields"
      title={COLLAPSE_TITLE_BASE}
      id={id}
      subtitle={value.numberDecision}
      isNew={!value.paperless && isNewResultDecision}
      openByDefault={isNewResultDecision || isExpandAllInPrescription}
      onClose={getOnClose(value)}
      tooltipText="Судебным решением осуществлена отмена (изменение) решения "
      showWarningTooltip={isDecisionCancelled}
      showLeftWarningMarker={isDecisionCancelled}
      headerPostfix={
        isLaterThanViolationsInProd ? (
          <ResultDecisionStatus
            status={displayStatus}
            isNew={!!value.isNew}
            isPaperless={!!value.paperless}
            agreementStatus={agreementStatus}
            isPendingD10Cancellation={isPendingD10Cancellation}
          />
        ) : undefined
      }
      changeOpenedCallback={setIsKnmCollapseOpened}
      error={isKnmCollapseOpened && hasError}
    >
      <>
        <Row>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: 'Вид решения',
              field: `${name}.kindDecision`,
              isKnm: true,
            })}
            tooltip={
              <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/kindDecision/name`} fieldId={kindDecisionBlockId} />
            }
          >
            <SelectInputFieldAutoOptions
              name={`${name}.kindDecision`}
              size="medium"
              id={kindDecisionBlockId}
              optionName={CommonCatalogsNamesEnum.ERKNM_KIND_RESULT_DECISION}
              transformFunc={transformKindResultDecisionsSelectOptions}
              isClearable={false}
              disabled={
                watchOnly || !!value?.signed || !!value?.prescriptionSigned || (isPaperless && !!value.document)
              }
              onChange={handleKindDecisionChange}
              warning={warningText}
            />
          </FieldBlock>
        </Row>

        {value.kindDecision &&
          (isPaperless &&
          (kindDecisionCode === KindResultDecisionCodesEnum.D_7 ||
            kindDecisionCode === KindResultDecisionCodesEnum.D_1) ? (
            <ErknmResultDecisionsItemPaperlessContent
              resultDecisionId={id}
              name={name}
              acts={acts}
              isWatchOnly={watchOnly}
              value={value}
              resultDecisions={resultDecisions}
              kindKnmCode={kindKnmCode}
              organizationsTemplatesSelectOptions={organizationsTemplatesSelectOptions}
              decisionIndex={decisionIndex}
              addRequirementsNpa={addRequirementsNpa}
              actsRequirementsNpa={actsRequirementsNpa}
              formRef={formRef}
              isPaperless={isPaperless}
              actsValue={actsValue}
              isLater4111ForDecision={isLater4111ForDecision}
            />
          ) : (
            <ErknmResultDecisionsItemContent
              acts={acts}
              actsRequirementsNpa={actsRequirementsNpa}
              addRequirementsNpa={addRequirementsNpa}
              decisionIndex={decisionIndex}
              formRef={formRef}
              id={id}
              kindKnmCode={kindKnmCode}
              knoOrganizationAddress={knoOrganizationAddress}
              knoOrganizationPhone={knoOrganizationPhone}
              knoOrganizationTitle={knoOrganizationTitle}
              name={name}
              organizationsTemplatesSelectOptions={organizationsTemplatesSelectOptions}
              value={value}
              isWatchOnly={watchOnly}
              eventsErknm={eventsErknm}
              organizations={organizations}
              lateFieldName={lateFieldName}
              isLater4111ForDecision={isLater4111ForDecision}
              actsValue={actsValue}
              resultDecisions={resultDecisions}
            />
          ))}
      </>
    </KnmCollapse>
  );
};
